<template>
  <div class="ur-stat">
    <!-- 篩選列 -->
    <div class="ur-stat-filters">
      <!-- 搜尋房屋編號 -->
      <input
        v-model="search"
        class="ur-stat-input"
        :placeholder="$t('URBANRENEWAL_ADMIN.STAT.SEARCH_HOUSE')"
      />

      <!-- 選配分類 -->
      <span class="ur-stat-filter-label">{{ $t('URBANRENEWAL_ADMIN.STAT.FILTER_CATEGORY') }}</span>
      <select v-model="category" class="ur-stat-select">
        <option value="">{{ $t('URBANRENEWAL_ADMIN.STAT.ALL') }}</option>
        <option value="normal">{{ $t('URBANRENEWAL_ADMIN.STAT.CAT_NORMAL') }}</option>
        <option value="non_priority">{{ $t('URBANRENEWAL_ADMIN.STAT.CAT_NONPRIORITY') }}</option>
        <option value="over">{{ $t('URBANRENEWAL_ADMIN.STAT.CAT_OVER') }}</option>
      </select>

      <!-- 右側：統計數 + 匯出 -->
      <span class="ur-stat-spacer"></span>
      <span class="ur-stat-count">{{ $t('URBANRENEWAL_ADMIN.STAT.COUNT_ROWS', { count: filteredRows.length }) }}</span>
      <button class="ur-lo-btn" @click="exportData">
        {{ $t('URBANRENEWAL_ADMIN.STAT.EXPORT') }}
      </button>
    </div>

    <!-- 錯誤提示條 -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- 讀取中 -->
    <div v-if="loading" class="ur-lo-loading">
      {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LOADING') }}
    </div>

    <!-- 表格 -->
    <div v-else class="ur-table-wrap">
      <table class="ur-table">
        <thead>
          <tr>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.HS_COL_ZONE') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.HS_COL_CODE') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.HS_COL_NORMAL') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.HS_COL_NONPRIORITY') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.HS_COL_OVER') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.COL_GROUP_COUNT') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in pagedRows" :key="idx">
            <td>{{ row.priority_zone || $t('URBANRENEWAL_ADMIN.STAT.DASH') }}</td>
            <td>{{ row.unit_code }}</td>
            <td>{{ joinList(row.normal_owners) }}</td>
            <td>{{ joinList(row.non_priority_owners) }}</td>
            <td>{{ joinList(row.over_owners) }}</td>
            <td>{{ groupCount(row) }} {{ $t('URBANRENEWAL_ADMIN.STAT.UNIT_GROUP') }}</td>
          </tr>
          <tr v-if="filteredRows.length === 0 && !loading">
            <td colspan="6" class="ur-td-empty">
              {{ projectId ? $t('URBANRENEWAL_ADMIN.STAT.NO_DATA') : $t('URBANRENEWAL_ADMIN.STAT.SELECT_PROJECT') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁列（共用元件，設計稿樣式）-->
    <UrbanRenewalPagination
      v-if="!loading && filteredRows.length"
      :total="filteredRows.length"
      :page.sync="page"
      :rpp.sync="rpp"
    />
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';
import UrbanRenewalPagination from '@/components/urbanrenewal/UrbanRenewalPagination.vue';

export default {
  name: 'HousingStatTab',

  components: { UrbanRenewalPagination },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      rows: [],
      loading: false,
      errorMsg: '',
      search: '',
      category: '',
      page: 1,
      rpp: 20,
    };
  },

  computed: {
    filteredRows() {
      let result = this.rows;
      if (this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        result = result.filter(row => row.unit_code && row.unit_code.toLowerCase().includes(q));
      }
      if (this.category) {
        if (this.category === 'normal') {
          result = result.filter(row => (row.normal_count || 0) > 0);
        } else if (this.category === 'non_priority') {
          result = result.filter(row => (row.non_priority_count || 0) > 0);
        } else if (this.category === 'over') {
          result = result.filter(row => (row.over_count || 0) > 0);
        }
      }
      return result;
    },

    pagedRows() {
      const start = (this.page - 1) * this.rpp;
      return this.filteredRows.slice(start, start + this.rpp);
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    search() { this.page = 1; },
    category() { this.page = 1; },
  },

  methods: {
    async load() {
      if (!this.projectId) {
        this.rows = [];
        return;
      }
      this.search = '';
      this.category = '';
      this.loading = true;
      try {
        const res = await UrbanRenewalDataService.getHousingStatistics(this.projectId);
        this.rows = res.data;
        this.page = 1;
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.STAT.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },

    async exportData() {
      try {
        const res = await UrbanRenewalDataService.exportHousingStatistics(this.projectId);
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        let fn = '房屋統計.xlsx';
        const cd = res.headers['content-disposition'];
        if (cd) {
          const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
          if (m) fn = decodeURIComponent(m[1]);
        }
        a.download = fn;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.STAT.EXPORT_FAILED');
      }
    },

    joinList(arr) {
      // 每組以（）框起,保留組邊界(如「（王一、王二）（陳三）」)
      return arr && arr.length ? arr.map((s) => '（' + s + '）').join('') : '-';
    },

    groupCount(row) {
      return (row.normal_count || 0) + (row.non_priority_count || 0) + (row.over_count || 0);
    },
  },
};
</script>
